import { AppWindow, ArrowRight } from "lucide-react";

import { STUDIO_NAME, deriveUpdatedLabel } from "@domain/studio-app";

import { MiniAppThumb } from "./StudioLibrary";

import type { StudioAppListItem } from "@/hooks/useStudioApps";

export interface StudioWorldWindowUIProps {
  apps: StudioAppListItem[];
  isLoading: boolean;
  /** Open one app's live preview inside the window. */
  onOpenApp: (appDir: string) => void;
  /** Deep-link to the full /studio page — the escape hatch for real work. */
  onOpenStudio: () => void;
}

/**
 * Presentational half of the Studio world window (NotebookQuickView triple,
 * studio spec §4.4) — the mockup's list JSX preserved verbatim, rows fed from
 * `useStudioApps` list items by the `.container`.
 */
export function StudioWorldWindowUI({ apps, isLoading, onOpenApp, onOpenStudio }: StudioWorldWindowUIProps) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-black/5 px-1 pb-2.5 dark:border-white/10">
        <AppWindow className="h-[18px] w-[18px] text-brand-coral-dark dark:text-brand-coral" strokeWidth={2} />
        <span className="text-[15px] font-bold text-black dark:text-white">{STUDIO_NAME}</span>
        <span className="ml-auto text-[12px] font-medium text-black/40 dark:text-white/40">
          {apps.length} apps
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-1.5">
        {!isLoading && apps.length === 0 ? (
          <p className="px-3 py-6 text-center text-[12px] text-black/45 dark:text-white/45">
            Nothing built yet — ask your agent for an app.
          </p>
        ) : (
          <ul className="space-y-1.5 px-1">
            {apps.map((app) => (
              <li key={app.appDir}>
                <button
                  type="button"
                  onClick={() => onOpenApp(app.appDir)}
                  className="flex w-full items-center gap-3 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
                >
                  <MiniAppThumb
                    html={app.entryHtml}
                    className="h-11 w-16 shrink-0 rounded-lg border border-black/8 dark:border-white/10"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-medium text-black dark:text-white">
                      {app.name}
                    </span>
                    <span className="block text-[11px] text-black/45 dark:text-white/45">
                      {app.updatedAt === null
                        ? ""
                        : deriveUpdatedLabel({ updatedAt: app.updatedAt, now: Date.now() })}
                    </span>
                  </span>
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                      app.share !== null && app.share.active
                        ? "bg-accent-green"
                        : "bg-black/20 dark:bg-white/20"
                    }`}
                    title={app.share !== null && app.share.active ? "Shared" : "Private"}
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <button
        onClick={onOpenStudio}
        className="mt-1.5 flex shrink-0 items-center justify-center gap-1.5 rounded-xl border border-black/10 py-2.5 text-[13px] font-bold text-black/70 transition-colors hover:border-brand-coral/50 hover:text-brand-coral-dark dark:border-white/15 dark:text-white/70 dark:hover:border-brand-coral/60 dark:hover:text-brand-coral"
      >
        Open full {STUDIO_NAME}
        <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
      </button>
    </div>
  );
}
